import { showNotification } from "@mantine/notifications";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { customAxios } from "lib/axios";

export type ProjectMember = {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  completedWelcome: boolean;
  isDemo: boolean;
  createdAt: string;
  updatedAt: string;
  __v: number;
  org: string;
  position: string;
  role: string;
};

export type Issue = {
  _id: string;
  project: string;
  type: string;
  priority: string;
  status: string;
  title: string;
  reporter: string;
  assignee?: string;
  createdAt: string;
  updatedAt: string;
  dueDate?: string;
  completedAt?: string;
  __v: number;
};

export interface ProjectResponse {
  _id: string;
  name: string;
  members: ProjectMember[];
  description: string;
  category: string;
  todoIssues: Issue[];
  inReviewIssues: Issue[];
  inProgressIssues: Issue[];
  completedIssues: Issue[];
  client: string;
  __v: number;
  approvals?: string;
  assumptions?: string;
  changeManagementProcess?: string;
  constraints?: string;
  exclusions?: string;
  inclusions?: string;
  projectObjectives?: string;
  projectOverview?: string;
  risksMitigation?: string;
  scopeOfWork?: string;
  stakeholdersInfo?: string;
  successCriteria?: string;
  timelineMilestones?: string;
}

async function fetchProject(
  projectId: string,
  userId: string
): Promise<ProjectResponse> {
  const url = `/projects/${projectId}?userId=${userId}`;
  const { data } = await customAxios.get<ProjectResponse>(url);
  return data;
}

export function useProject(
  projectId: string,
  userId: string,
  options?: { enabled?: boolean }
) {
  return useQuery(
    ["project", projectId, userId],
    () => fetchProject(projectId, userId),
    {
      enabled: options?.enabled ?? true,
      staleTime: 0,
      cacheTime: 0,
      refetchOnReconnect: true,
      refetchOnWindowFocus: true,
      refetchOnMount: "always",
      onError: (error: AxiosError) => {
        showNotification({
          title: "Error fetching project",
          message: error.message || "Unable to load project data.",
          color: "red",
        });
      },
      // cacheTime:false,
    }
  );
}
